import { formatUSPhoneNumber } from '@user/features/GlobalSearch/ResultItem/utils'
import { useMemo, useState } from 'react'
import { MultiTenantLocationDetails } from '@user/store/map/typings'
import { formatAddress } from '@user/features/MapView/utils/formatAddress'
import { useSelector } from 'react-redux'
import {
  selectEntityStatus,
  selectServiceLocationId,
  selectServiceLocationsDetails,
} from '@user/store/map'

export type Props = {
  location: MultiTenantLocationDetails
}

export const getSalesFunnelStage = (
  entityType: string,
  salesFunnelStage?: string | null,
) => {
  if (entityType === 'Lead' || entityType === 'Opportunity') {
    return salesFunnelStage || ''
  }

  return ''
}

export function useData(props: Props) {
  const { status } = useSelector(selectServiceLocationsDetails)
  const selectedEntityStatus = useSelector(selectEntityStatus)
  const selectedServiceLocationId = useSelector(selectServiceLocationId)
  const { location } = props
  const {
    company,
    firstName,
    lastName,
    contact,
    entityType: variant,
    phoneNumber,
    entityId,
    serviceLocationId,
    locationCode,
    externalLinkId,
    hasViewPermission,
    salesFunnelStage,
    knockStatusDetails,
  } = location
  const [openKnockStatusModal, setOpenKnockStatusModal] = useState(false)
  const nameAndCompanyDisplay = useMemo(() => {
    if (!location) {
      return ''
    }
    const companyName = company
    const name = `${firstName} ${lastName}`
    const items = [name, companyName].filter((x) => x && x.trim().length > 0)
    if (items.length > 1) {
      return items.join(' / ')
    } else {
      return items[0] || ''
    }
  }, [company, firstName, lastName, location])

  const addressDisplay = useMemo(
    () =>
      contact
        ? formatAddress(
            contact.street1,
            contact.street2,
            contact.city,
            contact.stateAbbreviation,
            contact.postalCode,
          )
        : '',
    [contact],
  )

  const entityType = useMemo(() => {
    switch (variant?.toLowerCase()) {
      case 'lead':
        return 'Lead'
      case 'opportunity':
        return 'Opportunity'
      case 'draftlead':
        return 'Pending Lead'
      default:
        return 'Unknown'
    }
  }, [variant])

  const formattedPhone = useMemo(() => {
    if (!phoneNumber) {
      return ''
    }
    const phone = phoneNumber
    return phone ? formatUSPhoneNumber(phone).trim() || phone : ''
  }, [phoneNumber])

  const salesFunnelStageStatus = useMemo(() => {
    return getSalesFunnelStage(entityType, salesFunnelStage)
  }, [entityType, salesFunnelStage])

  const knockStatus = useMemo(() => {
    return {
      id: knockStatusDetails?.id ?? null,
      isDefault: knockStatusDetails?.knockStatusSettingsName === 'Do not knock',
      status: knockStatusDetails?.knockStatusSettingsName || 'None',
      hasActiveStatus: Boolean(knockStatusDetails?.knockStatusSettingsName),
      note: knockStatusDetails?.mostRecentNote || null,
      createdTime: knockStatusDetails?.createdTime || null,
      createdByName: knockStatusDetails?.createdByName || null,
      knockStatusCount: knockStatusDetails?.knockStatusCount ?? 0,
      knockStatusLocationId: knockStatusDetails?.knockStatusLocationId ?? null,
    }
  }, [knockStatusDetails])

  const knockStatusLocationId =
    knockStatusDetails?.knockStatusLocationId ?? null
  const hasKnockStatus =
    Boolean(knockStatusDetails?.knockStatusSettingsName) &&
    knockStatusLocationId !== null

  return {
    addressDisplay,
    entityType,
    formattedPhone,
    nameAndCompanyDisplay,
    variant: variant.toLowerCase(),
    status,
    serviceLocationId,
    entityId,
    locationCode,
    externalLinkId,
    hasViewPermission,
    salesFunnelStage: salesFunnelStageStatus,
    openKnockStatusModal,
    setOpenKnockStatusModal,
    selectedEntityStatus,
    selectedServiceLocationId,
    knockStatus,
    knockStatusLocationId,
    hasKnockStatus,
  }
}
